import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import {
  DatePicker,
  Form,
  Input,
  Radio,
  type RadioChangeEvent,
  Select,
  Typography
} from '@vivaldis/antd-ui'
import { localizedCountryNameSelector, useLocale } from '@vivaldis/common'
import { Grid } from '../../../../../Grid'
import { useForm } from '../../../../provider/FormProvider'
import { CountryType } from '../../../../types/CountryType'

interface Props {
  countries: CountryType[]
}

export const AdditionalInformationSection = ({ countries }: Props) => {
  const [t] = useTranslation('web_ui')

  const locale = useLocale()

  const { values, setFieldValue } = useForm()

  const countryOptions = useMemo(
    () =>
      countries
        .map(country => ({
          label: localizedCountryNameSelector(locale, country),
          value: country.id
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [countries, locale]
  )

  const isRetiredOptions = useMemo(
    () => [
      {
        label: t(
          'edit_employee_general_information_form.is_retired.options.yes'
        ),
        value: true
      },
      {
        label: t(
          'edit_employee_general_information_form.is_retired.options.no'
        ),
        value: false
      }
    ],
    [t]
  )

  const handleIsRetiredChange = useCallback(
    (e: RadioChangeEvent) => {
      const selectedValue = e.target.value
      if (!selectedValue) {
        setFieldValue('retiredOn', null)
      }
    },
    [setFieldValue]
  )

  return (
    <>
      <Typography.Title level={5} marginBottom={16}>
        {t(
          'edit_employee_general_information_form.additional_information_section_header'
        )}
      </Typography.Title>
      <Grid columns={2} gap={0} columnGap={16} marginTop={6}>
        <Form.Item
          name="placeOfBirth"
          required
          label={t(
            'edit_employee_general_information_form.place_of_birth.label'
          )}
        >
          <Input
            name="placeOfBirth"
            placeholder={t('form.required')}
            autoComplete="off"
          />
        </Form.Item>
        <Form.Item
          name="countryOfBirthId"
          required
          label={t(
            'edit_employee_general_information_form.country_of_birth_id.label'
          )}
        >
          <Select
            name="countryOfBirthId"
            placeholder={t('form.required')}
            options={countryOptions}
            showSearch
            autoComplete="chrome-off-employee-country"
          />
        </Form.Item>
      </Grid>
      <Grid columns={2} gap={0} columnGap={16} marginTop={6}>
        <Form.Item
          name="mobile"
          required
          label={t('edit_employee_general_information_form.mobile.label')}
        >
          <Input
            name="mobile"
            placeholder={t('form.required')}
            autoComplete="off"
          />
        </Form.Item>

        <Form.Item
          name="email"
          required
          label={t('edit_employee_general_information_form.email.label')}
        >
          <Input
            name="email"
            placeholder={t('form.required')}
            autoComplete="off"
          />
        </Form.Item>
      </Grid>
      <Form.Item
        name="isRetired"
        required
        label={t('edit_employee_general_information_form.is_retired.label')}
      >
        <Radio.Group
          name="isRetired"
          size="large"
          options={isRetiredOptions}
          onChange={handleIsRetiredChange}
        />
      </Form.Item>
      {values.isRetired ? (
        <Form.Item
          name="retiredOn"
          label={t('edit_employee_general_information_form.retired_on.label')}
          required
        >
          <DatePicker
            name="retiredOn"
            format="dd-MM-yyyy"
            placeholder={t('form.required')}
            style={{ width: '100%' }}
            autoComplete="off"
          />
        </Form.Item>
      ) : null}
    </>
  )
}
